import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, ScrollText } from "lucide-react";
import { fmtLocalDTFull } from "@/lib/clinical";

export default function BillingAuditLog() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ action: "", user: "", from: "", to: "", patient_id: "", limit: 200 });

  const params = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k === "from" ? "from_" : k, v); });
    return p.toString();
  }, [f]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/billing/audit-logs?${params}`);
      setRows(data.rows);
      setActions(data.actions);
      setUsers(data.users);
    } catch (e) {
      toast.error("Failed to load audit trail");
    } finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-6" data-testid="billing-audit-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Billing</div>
        <h1 className="font-display text-3xl font-semibold mt-1">Audit trail</h1>
        <p className="text-sm text-muted-foreground mt-1">Every billing action is logged — nothing is ever deleted.</p>
      </div>

      <div className="card-clinical p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label>Action</Label>
            <Select value={f.action || "__all"} onValueChange={(v) => set("action", v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="audit-action"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All actions</SelectItem>
                {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>User</Label>
            <Select value={f.user || "__all"} onValueChange={(v) => set("user", v === "__all" ? "" : v)}>
              <SelectTrigger data-testid="audit-user"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All users</SelectItem>
                {users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={f.from} onChange={(e) => set("from", e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={f.to} onChange={(e) => set("to", e.target.value)} />
          </div>
          <div>
            <Label>Patient ID</Label>
            <Input value={f.patient_id} onChange={(e) => set("patient_id", e.target.value)} placeholder="Optional" />
          </div>
          <div className="flex items-end">
            <Button className="rounded-full w-full" size="sm" onClick={load} data-testid="audit-refresh">
              <RefreshCw size={13} className="mr-1.5" /> Reload
            </Button>
          </div>
        </div>
      </div>

      <div className="card-clinical p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <ScrollText size={14} className="text-primary" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Events ({rows.length})</span>
        </div>
        {loading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && <div className="p-6 text-sm text-muted-foreground">No events for these filters.</div>}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Action</th>
                  <th className="text-left px-3 py-2 font-medium">User</th>
                  <th className="text-left px-3 py-2 font-medium">Role</th>
                  <th className="text-left px-3 py-2 font-medium">Bill ID</th>
                  <th className="text-left px-3 py-2 font-medium">Old</th>
                  <th className="text-left px-3 py-2 font-medium">New</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 tabular text-[12px] whitespace-nowrap">{fmtLocalDTFull(r.at)}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="rounded-full text-[10px]">{r.action}</Badge></td>
                    <td className="px-3 py-2">{r.username || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground text-[12px]">{r.role}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground truncate max-w-[10ch]" title={r.bill_id}>{r.bill_id?.slice(0, 8) || "—"}</td>
                    <td className="px-3 py-2 max-w-md">
                      <pre className="text-[11px] whitespace-pre-wrap break-words text-muted-foreground line-clamp-3">
                        {r.old ? JSON.stringify(r.old, null, 0).slice(0, 200) : "—"}
                      </pre>
                    </td>
                    <td className="px-3 py-2 max-w-md">
                      <pre className="text-[11px] whitespace-pre-wrap break-words text-foreground line-clamp-3">
                        {r.new ? JSON.stringify(r.new, null, 0).slice(0, 200) : "—"}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
